<script setup>
/**
 * Editing a day's written entry (journal redesign 2026-10-27, build note 8):
 * everything the old Journal's focused editor did, now behind the entry's
 * Edit. Formatting, undo / redo through the drafts, draft history with
 * Preview and Restore, the reflective lenses (Mystic Aida answers in the
 * assistant beside it), Consolidate after appended thoughts, and the entry
 * length for new entries. Saves as the seeker types (journal/spoken).
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { store } from '@/store'
import { fullDate } from '@/utils/journal-book'
import { setJournalLength, journalVersions, journalRestore, journalSpoken, journalReflect, journalSetVersion, journalConsolidate } from '@/api-service'

const props = defineProps({
  dayKey: { type: String, required: true },
  text: { type: String, default: '' },
  wordCount: { type: Number, default: 80 },
})
const emit = defineEmits(['close', 'saved', 'length'])
const { notify } = useNotification()

const editorRef = ref(null)
const versionList = ref([])
const versionsOpen = ref(false)
const loadingVersions = ref(false)
const restoringVersion = ref(0)
const previewVersion = ref(0)
const vPointer = ref(0)
const hasSel = ref(false)
const reflecting = ref('')
const reflectOpen = ref(false)
const reflectRef = ref(null)
const consolidating = ref(false)
const current = ref(props.text)
let saveTimer = null

const SOURCE_LABEL = { original: 'Original', magic: 'Magic', spoken: 'Spoken', rebuild: 'Rebuilt' }
const ASSIST = [
  { lens: 'go_deeper', icon: '🔍', label: 'Go deeper' },
  { lens: 'name_feeling', icon: '💗', label: 'Name the feeling' },
  { lens: 'reflect_back', icon: '🪞', label: 'Reflect back' },
  { lens: 'find_pattern', icon: '🔁', label: 'Find the pattern' },
  { lens: 'cards', icon: '🔮', label: 'Through the cards' },
  { lens: 'avoiding', icon: '🌑', label: "What I'm avoiding" },
]
const selectedLens = ref(ASSIST[0])

const canUndo = computed(() => vPointer.value < versionList.value.length - 1)
const canRedo = computed(() => vPointer.value > 0)
const hasAppends = computed(() => /j-added/.test(current.value || ''))
function took (journal) {
  if (!journal) return
  current.value = journal.summary || journal.text || ''
  emit('saved', { key: props.dayKey, text: current.value })
}

async function loadVersions () {
  loadingVersions.value = true
  try { const d = await journalVersions(store.session_id, props.dayKey); versionList.value = (d && d.versions) || [] } catch (e) { versionList.value = [] } finally { loadingVersions.value = false }
}
async function applyVersion (v) {
  clearTimeout(saveTimer)
  try {
    const r = await journalSetVersion(store.session_id, props.dayKey, v.id)
    if (r && r.ok && r.journal) { if (editorRef.value) editorRef.value.innerHTML = r.journal.summary || ''; took(r.journal) }
  } catch (e) { /* the draft stays as it is */ }
}
function undo () { if (canUndo.value) { vPointer.value += 1; applyVersion(versionList.value[vPointer.value]) } }
function redo () { if (canRedo.value) { vPointer.value -= 1; applyVersion(versionList.value[vPointer.value]) } }
async function restoreVersion (v) {
  if (restoringVersion.value) return
  restoringVersion.value = v.id
  try {
    const r = await journalRestore(store.session_id, props.dayKey, v.id)
    if (r && r.ok && r.journal) {
      if (editorRef.value) editorRef.value.innerHTML = r.journal.summary || ''
      took(r.journal)
      notify({ text: 'Draft restored', type: 'success' })
      versionsOpen.value = false
      await loadVersions(); vPointer.value = 0
    }
  } catch (e) { /* */ } finally { restoringVersion.value = 0 }
}
function fmtVersionTime (iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch (e) { return '' }
}

function fmt (cmd) { document.execCommand(cmd, false, null); editorRef.value && editorRef.value.focus(); updateSel() }
function updateSel () { const t = ((window.getSelection && window.getSelection().toString()) || '').trim(); hasSel.value = t.length > 1 }
function onEditorInput () { clearTimeout(saveTimer); saveTimer = setTimeout(saveEditor, 1200) }
async function saveEditor () {
  if (!editorRef.value) return
  const html = editorRef.value.innerHTML
  try {
    const r = await journalSpoken(store.session_id, props.dayKey, html)
    if (r && r.ok) { took(r.journal || { summary: html }); await loadVersions(); vPointer.value = 0 }
  } catch (e) { /* kept in the editor; the next keystroke tries again */ }
}
function pickLens (a) { selectedLens.value = a; reflectOpen.value = false; if (hasSel.value) askLens(a) }
async function askLens (a) {
  if (!hasSel.value || reflecting.value) return
  const selection = ((window.getSelection && window.getSelection().toString()) || '').trim()
  reflecting.value = a.lens
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: true, scope: 'journal', date: props.dayKey } }))
  try {
    const r = await journalReflect(store.session_id, props.dayKey, { selection, lens: a.lens })
    window.dispatchEvent(new CustomEvent('journal-reflection', { detail: { lensLabel: a.label, selection, text: (r && r.reflection) || '' } }))
  } catch (e) { /* */ } finally { reflecting.value = '' }
}
async function consolidate () {
  if (consolidating.value) return
  consolidating.value = true
  clearTimeout(saveTimer)
  try {
    const r = await journalConsolidate(store.session_id, props.dayKey)
    if (r && r.ok && r.journal) {
      if (editorRef.value) editorRef.value.innerHTML = r.journal.summary || ''
      took(r.journal); await loadVersions(); vPointer.value = 0
      notify({ text: 'Thoughts consolidated', type: 'success' })
    }
  } catch (e) { notify({ text: 'Could not consolidate just now', type: 'error' }) } finally { consolidating.value = false }
}
// Mystic Aida's "find the words" lands in the entry, highlighted, under the passage it answers.
function onJournalAppend (e) {
  const { date, text, selection } = e.detail || {}
  if (!text || date !== props.dayKey || !editorRef.value) return
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const quoted = (selection || '').trim()
  editorRef.value.innerHTML = (editorRef.value.innerHTML || '') + '<div class="j-added">' + (quoted ? `<span class="j-added-ref">“${esc(quoted)}”</span>` : '') + `<span class="j-added-text">${esc(text)}</span></div>`
  saveEditor()
}
function onDocClick (e) { if (reflectOpen.value && reflectRef.value && !reflectRef.value.contains(e.target)) reflectOpen.value = false }

// Entry length for new entries (was the pencil beside the old search box).
const words = ref(props.wordCount)
const lengthOpen = ref(false)
const savingLength = ref(false)
watch(() => props.wordCount, (n) => { words.value = n })
async function saveLength () {
  if (savingLength.value) return
  savingLength.value = true
  try {
    const r = await setJournalLength(store.session_id, words.value)
    if (r && r.ok) { words.value = r.word_count; emit('length', r.word_count); notify({ text: 'Journal length saved - applies to new entries', type: 'success' }); lengthOpen.value = false }
  } catch (e) { /* */ } finally { savingLength.value = false }
}

function close () {
  clearTimeout(saveTimer)
  if (editorRef.value && editorRef.value.innerHTML !== current.value) saveEditor()
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
  emit('close')
}

onMounted(async () => {
  await nextTick()
  if (editorRef.value) editorRef.value.innerHTML = props.text || ''
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: true, scope: 'journal', date: props.dayKey, words: props.wordCount } }))
  window.addEventListener('journal-append', onJournalAppend)
  document.addEventListener('click', onDocClick)
  await loadVersions(); vPointer.value = 0
})
onUnmounted(() => {
  clearTimeout(saveTimer)
  window.removeEventListener('journal-append', onJournalAppend)
  document.removeEventListener('click', onDocClick)
})
</script>

<template>
  <div class="jed" data-testid="journal-editor">
    <div class="head">
      <button type="button" class="back" data-testid="journal-editor-done" @click="close">‹ Done</button>
      <span class="when">{{ fullDate(dayKey) }}</span>
    </div>
    <div class="toolbar">
      <button type="button" class="tb" title="Bold" @mousedown.prevent @click="fmt('bold')"><b>B</b></button>
      <button type="button" class="tb" title="Italic" @mousedown.prevent @click="fmt('italic')"><em>I</em></button>
      <button type="button" class="tb" title="Underline" @mousedown.prevent @click="fmt('underline')"><u>U</u></button>
      <button type="button" class="tb" title="Bulleted list" @mousedown.prevent @click="fmt('insertUnorderedList')">•</button>
      <span class="sep"></span>
      <button type="button" class="tb" :disabled="!canUndo" title="Undo" aria-label="Undo" @mousedown.prevent @click="undo">↶</button>
      <button type="button" class="tb" :disabled="!canRedo" title="Redo" aria-label="Redo" @mousedown.prevent @click="redo">↷</button>
      <button type="button" class="tb wide" :class="{ on: versionsOpen }" :disabled="!versionList.length" data-testid="journal-versions-btn" @mousedown.prevent @click="versionsOpen = !versionsOpen">Versions</button>
      <button type="button" class="tb wide" :class="{ on: lengthOpen }" data-testid="journal-length-btn" @mousedown.prevent @click="lengthOpen = !lengthOpen">Length</button>
      <span class="sep"></span>
      <div ref="reflectRef" class="reflect">
        <button type="button" class="tb wide" @mousedown.prevent @click="reflectOpen = !reflectOpen">{{ reflecting ? 'Reflecting…' : (selectedLens.icon + ' ' + selectedLens.label) }} ▾</button>
        <div v-if="reflectOpen" class="menu">
          <p class="menu-hint">Highlight words in the entry, then pick a lens.</p>
          <button v-for="a in ASSIST" :key="a.lens" type="button" :disabled="!hasSel || !!reflecting" @mousedown.prevent @click="pickLens(a)">{{ a.icon }} {{ a.label }}</button>
        </div>
      </div>
    </div>

    <div v-if="lengthOpen" class="panel" data-testid="journal-length">
      <div class="panel-head"><span>Entry length · {{ words }} words</span><span class="fine">applies to new entries</span></div>
      <input v-model.number="words" type="range" min="80" max="500" step="10" aria-label="Entry length in words" />
      <div class="row"><button type="button" class="btn-ink" :disabled="savingLength" @click="saveLength">{{ savingLength ? 'Saving…' : 'Save' }}</button><button type="button" class="ilink" @click="lengthOpen = false">Cancel</button></div>
    </div>

    <div v-if="versionsOpen" class="panel" data-testid="journal-versions">
      <div class="panel-head"><span>Versions</span><button type="button" class="ilink" @click="versionsOpen = false">Close</button></div>
      <p v-if="loadingVersions" class="fine">Loading drafts…</p>
      <p v-else-if="!versionList.length" class="fine">No earlier drafts yet.</p>
      <div v-for="v in versionList" v-else :key="v.id" class="ver">
        <div class="ver-meta">
          <span class="src">{{ SOURCE_LABEL[v.source] || v.source }}</span><span class="fine">{{ fmtVersionTime(v.created_at) }}</span>
          <button type="button" class="ilink" @click="previewVersion = previewVersion === v.id ? 0 : v.id">{{ previewVersion === v.id ? 'Hide' : 'Preview' }}</button>
          <button type="button" class="ilink" :disabled="restoringVersion === v.id" data-testid="journal-version-restore" @click="restoreVersion(v)">{{ restoringVersion === v.id ? '…' : 'Restore' }}</button>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -- the seeker's own earlier draft -->
        <p class="ver-text" :class="{ full: previewVersion === v.id }" v-html="v.summary"></p>
      </div>
    </div>

    <div v-if="hasAppends" class="row"><button type="button" class="btn-ghost" :disabled="consolidating" @mousedown.prevent @click="consolidate">{{ consolidating ? 'Consolidating…' : '✦ Consolidate my thoughts' }}</button></div>
    <div ref="editorRef" class="ed" contenteditable="true" role="textbox" aria-multiline="true" :aria-label="`The entry for ${fullDate(dayKey)}`"
         @input="onEditorInput" @mouseup="updateSel" @keyup="updateSel"></div>
  </div>
</template>

<style scoped>
.jed { padding: 14px 4px 40px; color: var(--j-ink); }
.head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.back { font: 600 14px var(--j-ui); color: var(--j-ink2); background: none; border: 0; padding: 10px 0; cursor: pointer; }
.when { font: 600 10.5px var(--j-display); letter-spacing: .2em; text-transform: uppercase; color: var(--j-hand); }
.toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin: 6px 0 10px; padding: 6px; border-radius: 10px; background: rgba(255, 255, 255, .4); box-shadow: inset 0 0 0 1px rgba(42, 24, 69, .1); }
.tb { min-width: 34px; height: 34px; border-radius: 8px; border: 0; background: none; color: var(--j-ink); font: 600 15px var(--j-serif); cursor: pointer; padding: 0 8px; }
.tb.wide { font: 600 12.5px var(--j-ui); }
.tb:hover:not(:disabled), .tb.on { background: rgba(42, 24, 69, .08); }
.tb:disabled { opacity: .35; cursor: default; }
.sep { width: 1px; height: 20px; background: rgba(42, 24, 69, .15); margin: 0 4px; }
.reflect { position: relative; }
.menu { position: absolute; z-index: 9; left: 0; top: 100%; min-width: 220px; padding: 6px; border-radius: 10px; background: #fbf6ea; box-shadow: 0 10px 30px rgba(42, 24, 69, .3), 0 0 0 1px rgba(42, 24, 69, .1); }
.menu button { display: block; width: 100%; text-align: left; padding: 9px 10px; border-radius: 7px; font: 500 14px var(--j-ui); color: var(--j-ink); background: none; border: 0; cursor: pointer; }
.menu button:disabled { opacity: .45; cursor: default; }
.menu-hint { margin: 0; padding: 6px 10px; font: 500 12.5px/1.35 var(--j-ui); color: var(--j-ink3); }
.panel { margin: 0 0 12px; padding: 12px; border-radius: 10px; background: #fbf7ee; box-shadow: 0 4px 14px rgba(42, 24, 69, .15); }
.panel-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; font: 600 15px var(--j-serif); }
.panel input[type='range'] { width: 100%; margin: 10px 0; accent-color: #6d3fa3; }
.fine { font: 500 12.5px var(--j-ui); color: var(--j-ink3); margin: 0; }
.row { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 10px; }
.ver { padding: 8px 0; border-top: 1px solid rgba(42, 24, 69, .08); }
.ver-meta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.src { font: 600 12px var(--j-ui); color: var(--j-ink); }
.ver-text { margin: 4px 0 0; font: italic 500 15px/1.4 var(--j-serif); color: var(--j-ink2); max-height: 2.8em; overflow: hidden; }
.ver-text.full { max-height: none; }
.ed { min-height: 220px; outline: 0; padding: 8px 10px; border-radius: 6px; background: rgba(255, 255, 255, .35); box-shadow: inset 0 0 0 1px rgba(140, 102, 40, .4); font: italic 500 20px/32px var(--j-serif); color: var(--j-ink); }
.ed :deep(.j-added) { display: block; margin: 8px 0; padding: 4px 0 4px 10px; border-left: 2px solid rgba(140, 102, 40, .5); background: rgba(232, 196, 110, .14); }
.ed :deep(.j-added-ref) { display: block; color: var(--j-ink2); font-size: 16px; }
.ilink { font: 600 13px var(--j-ui); color: var(--j-ink); text-decoration: underline; text-underline-offset: 3px; background: none; border: 0; padding: 6px 0; cursor: pointer; }
.btn-ink { display: inline-flex; align-items: center; padding: 8px 18px; min-height: 40px; border-radius: 999px; background: linear-gradient(180deg, #6d3fa3, #4f2a80); color: #ffe7b5; font: 700 15px var(--j-serif); border: 0; cursor: pointer; }
.btn-ghost { display: inline-flex; align-items: center; padding: 7px 14px; min-height: 40px; border-radius: 999px; border: 1.3px solid rgba(42, 24, 69, .45); color: var(--j-ink); font: 600 14.5px var(--j-serif); background: none; cursor: pointer; }
</style>
